import { useEffect, useContext, useRef, useMemo, memo } from 'react';
import LoadingDots from '~/components/misc/loading-dots.js';
import BinaryQuestion from '~/components/binary-question';
import UIContext from '~/ui/ui-context.ts';
import { requestApi } from '~/services/api.js';
import { SWISH } from '../../beta/constants.js';
import useTranslations from '~/components/translation/use-translations';
import { ButtonWithoutBackground } from '@comeon/ui';
import styled from 'styled-components';

const StyledButtonTransparent = styled(ButtonWithoutBackground)`
	font: var(--label-regular);
	color: var(--cookie-policy-link);
	white-space: inherit;
	text-decoration: underline;
	padding: 0;
`;

const SwishLoader = memo(() => (
	<div className="swish-loader">
		<LoadingDots />
	</div>
));

const textKeys = [
	'SWISH_POPUP_HEADER',
	'SIIRTO_POPUP_HEADER',
	'SIIRTO_POPUP_TEXT',
	'SWISH_POPUP_TEXT',
	'SWISH_POPUP_QR_HEADER',
	'SWISH_POPUP_QR_TEXT_MOBILE',
	'SWISH_POPUP_QR_TEXT_DESKTOP',
	'SWISH_POPUP_APP_DISCLAIMER',
	'SWISH_POPUP_QR_LINK',
	'MUCHBETTER_POPUP_HEADER',
	'MUCHBETTER_POPUP_TEXT',
	'JETON_POPUP_HEADER',
	'JETON_POPUP_TEXT',
	'MOBILEPAY_POPUP_TEXT',
	'MOBILEPAY_POPUP_HEADER',
	'CANCEL',
	'JETON_APP_URL_TEXT',
	'MUCHBETTER_SIGNUP_LINK_TEXT'
];

const NativeAppPrompt = ({
	onNoClick,
	QRCodedata,
	selectedMethod,
	signUpUrl,
	accHistRef,
	quickDepositFlow,
	swishQRCodeFlowDesktop,
	swishMobileBeta,
	onPaymentSuccess,
	setQRCode
}) => {
	const { translations: t, isLoadingTranslations } = useTranslations(textKeys);
	const { withinSidebar = false } = useContext(UIContext);
	const Intervel = useRef();

	const pollPaymentStatus = async () => {
		const API_BASE_PATH = process.env.API_BASE_PATH || '';
		const payload = `accHistRef=${accHistRef}`;
		try {
			const response = await requestApi(
				`${API_BASE_PATH}/PaymentFormDataService/getDepositResult`,
				payload,
				'POST',
				true
			);
			if (response.state !== 'pending') {
				clearInterval(Intervel.current);
				return onPaymentSuccess();
			}
		} catch (_) {
			// FIXME: handle error case here
		}
	};

	useEffect(() => {
		Intervel.current = setInterval(pollPaymentStatus, 1000);
		return () => clearInterval(Intervel.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const title = useMemo(() => {
		if (isLoadingTranslations) {
			return;
		}
		switch (true) {
			case selectedMethod === 'SIIRTO':
				return t.SIIRTO_POPUP_HEADER;
			case selectedMethod === 'MUCHBETTER':
				return t.MUCHBETTER_POPUP_HEADER;
			case selectedMethod === 'JETON':
				return t.JETON_POPUP_HEADER;
			case selectedMethod === SWISH && (swishMobileBeta || swishQRCodeFlowDesktop):
				return t.SWISH_POPUP_QR_HEADER;
			default:
				return t.SWISH_POPUP_HEADER;
		}
	}, [swishMobileBeta, swishQRCodeFlowDesktop, isLoadingTranslations, selectedMethod]); // eslint-disable-line react-hooks/exhaustive-deps

	const text = useMemo(() => {
		if (isLoadingTranslations) {
			return;
		}
		switch (true) {
			case selectedMethod === 'SIIRTO':
				return t.SIIRTO_POPUP_TEXT;
			case selectedMethod === 'MUCHBETTER':
				return t.MUCHBETTER_POPUP_TEXT;
			case selectedMethod === 'JETON':
				return t.JETON_POPUP_TEXT;
			case selectedMethod === SWISH && swishMobileBeta && !QRCodedata:
				return (
					<>
						{t.SWISH_POPUP_APP_DISCLAIMER} <br /> <br />
						<StyledButtonTransparent onClick={setQRCode}>{t.SWISH_POPUP_QR_LINK}</StyledButtonTransparent>
					</>
				);
			case selectedMethod === SWISH && swishMobileBeta && !!QRCodedata:
				return (
					<>
						{t.SWISH_POPUP_QR_TEXT_DESKTOP} <br /> <br /> {t.SWISH_POPUP_QR_TEXT_MOBILE}
					</>
				);
			case selectedMethod === SWISH && swishQRCodeFlowDesktop:
				return t.SWISH_POPUP_QR_TEXT_DESKTOP;
			default:
				return t.SWISH_POPUP_TEXT;
		}
	}, [selectedMethod, swishQRCodeFlowDesktop, isLoadingTranslations, swishMobileBeta, QRCodedata]); // eslint-disable-line react-hooks/exhaustive-deps

	if (isLoadingTranslations) {
		return <div className="spinner" />;
	}

	return (
		<BinaryQuestion
			shouldOpenBinary
			title={title}
			text={text}
			onNoClick={onNoClick}
			noText={t.CANCEL}
			signUpUrl={signUpUrl}
			className={selectedMethod}
			appUrlText={t.JETON_APP_URL_TEXT}
			QRCodedata={QRCodedata}
			transactionLoader={swishMobileBeta ? <SwishLoader /> : null}
			signUpText={t.MUCHBETTER_SIGNUP_LINK_TEXT}
			openInSidebar={withinSidebar}
			renderInParent={quickDepositFlow}
		/>
	);
};

export default NativeAppPrompt;
